import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Clock, Stethoscope, Zap, Edit, Trash2, Flag, ExternalLink, Phone, Heart, Send, Users } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";
import HireBadge from "@/components/HireBadge";
import ApplyDialog from "@/components/ApplyDialog";
import MessageButton from "@/components/MessageButton";
import AdminRemoveButton from "@/components/AdminRemoveButton";
import StatusPill, { APPLICATION_STATUSES } from "@/components/StatusPill";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSavedJobs } from "@/hooks/useSavedJobs";

export default function JobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [applyOpen, setApplyOpen] = useState(false);
  const [applications, setApplications] = useState(null);
  const { isSaved, toggle: toggleSave } = useSavedJobs();

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => setJob(false));
  }, [id]);

  const isOwner = user && job && user.id === job.owner_id;
  useEffect(() => {
    if (!isOwner || !job) return;
    api.get(`/jobs/${job.id}/applications`).then((r) => setApplications(r.data.items)).catch(() => setApplications([]));
  }, [isOwner, job]);

  if (job === false) return <div className="max-w-4xl mx-auto p-10 text-center text-[#5C5C5C]">Job not found.</div>;
  if (!job) return <div className="max-w-4xl mx-auto p-10 text-center text-[#5C5C5C]">Loading…</div>;

  const saved = isSaved(job.id);

  const remove = async () => {
    if (!confirm("Delete this job posting?")) return;
    await api.delete(`/jobs/${job.id}`);
    toast.success("Job removed");
    nav("/jobs");
  };
  const submitReport = async () => {
    if (!reason.trim()) return toast.error("Please describe the reason");
    try {
      await api.post("/reports", { target_type: "job", target_id: job.id, reason, details });
      toast.success("Thanks — we'll review this posting.");
      setReason(""); setDetails("");
    } catch (err) {
      toast.error("Could not submit report. Please try again.");
    }
  };
  const onSaveClick = async () => {
    if (!user) { nav("/login"); return; }
    const r = await toggleSave(job.id);
    if (r?.saved) toast.success("Saved to your list");
    else if (r?.saved === false) toast.success("Removed from saved");
  };
  const onApplyClick = () => {
    if (!user) { nav("/login"); return; }
    setApplyOpen(true);
  };

  return (
    <div>
      <section className="bg-[#1E3A2F] text-[#F7F4EB] relative overflow-hidden">
        <div className="absolute inset-0 abm-kente opacity-[0.06]" />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 relative">
          <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-[#DDA74A] mb-4" data-testid="job-back-btn"><ArrowLeft className="w-4 h-4" /> Back to jobs</button>
          <div className="flex flex-wrap items-center gap-2">
            <span className="abm-chip !bg-[#DDA74A]/15 !text-[#DDA74A] !border-[#DDA74A]/40">
              <Stethoscope className="w-3 h-3" /> {job.role_category}
            </span>
            {job.start_asap && (
              <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full bg-[#C05A3D] text-white">
                <Zap className="w-3 h-3" /> Start immediately
              </span>
            )}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl mt-4 max-w-4xl leading-tight">{job.title}</h1>
          <div className="mt-2 text-lg text-[#A3B3AA] flex items-center gap-2 flex-wrap">
            <span>{job.employer}</span>
            {job.owner_verified && <VerifiedBadge tier={job.owner_verification_tier} />}
            {job.owner_hires_count > 0 && <HireBadge count={job.owner_hires_count} />}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10 grid lg:grid-cols-[1.4fr_0.6fr] gap-10">
        <div>
          <h2 className="font-display text-2xl text-[#1E3A2F]">About this role</h2>
          <p className="mt-4 whitespace-pre-wrap text-[#1A1A1A] leading-relaxed">{job.description}</p>
          {job.requirements && (
            <>
              <h2 className="font-display text-2xl text-[#1E3A2F] mt-8">Requirements</h2>
              <p className="mt-4 whitespace-pre-wrap text-[#1A1A1A] leading-relaxed">{job.requirements}</p>
            </>
          )}

          {isOwner && applications && (
            <div className="mt-10">
              <h2 className="font-display text-2xl text-[#1E3A2F] inline-flex items-center gap-2"><Users className="w-5 h-5" /> Applications ({applications.length})</h2>
              <div className="mt-4 space-y-3">
                {applications.length === 0 ? (
                  <div className="text-sm text-[#5C5C5C] py-6 text-center border border-dashed border-[#E2DED0] rounded-xl">No applicants yet — share the link with your network.</div>
                ) : applications.map((a) => (
                  <div key={a.id} data-testid={`application-${a.id}`} className="rounded-xl border border-[#E2DED0] bg-white p-4">
                    <div className="flex flex-wrap items-start gap-3 justify-between">
                      <div>
                        <div className="font-display text-lg text-[#1E3A2F] flex items-center gap-2 flex-wrap">
                          {a.name}
                          {a.applicant_verified && <VerifiedBadge size="sm" />}
                          <StatusPill status={a.status} />
                        </div>
                        <div className="text-sm text-[#5C5C5C] mt-1">
                          <a href={`mailto:${a.email}`} className="text-[#C05A3D]">{a.email}</a>
                          {a.phone && <span className="ml-3">{a.phone}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select
                          value={a.status || "new"}
                          onValueChange={async (v) => {
                            try {
                              await api.patch(`/applications/${a.id}/status`, { status: v });
                              setApplications((prev) => prev.map((x) => (x.id === a.id ? { ...x, status: v } : x)));
                              toast.success(`Marked as ${v}`);
                            } catch { toast.error("Could not update status"); }
                          }}
                        >
                          <SelectTrigger data-testid={`app-status-${a.id}`} className="w-[140px] h-9 rounded-full"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {APPLICATION_STATUSES.map((s) => <SelectItem key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <div className="text-xs text-[#5C5C5C]">{new Date(a.created_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                    <p className="mt-3 text-sm whitespace-pre-wrap">{a.pitch}</p>
                    {a.resume_path && (
                      <a
                        href={`/api/files/${a.resume_path}`}
                        target="_blank" rel="noreferrer"
                        className="mt-3 inline-flex items-center gap-1 text-sm text-[#1E3A2F] font-medium"
                      >
                        View resume <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="lg:sticky lg:top-24 h-fit rounded-2xl bg-white border border-[#E2DED0] p-6 space-y-4">
          <InfoRow label="Employment type">{job.employment_type}</InfoRow>
          {job.shift && <InfoRow icon={<Clock className="w-4 h-4" />} label="Shift">{job.shift}</InfoRow>}
          {job.pay_range && <InfoRow label="Pay">{job.pay_range}</InfoRow>}
          <InfoRow icon={<MapPin className="w-4 h-4" />} label="Location">
            <div>{job.location}</div>
            {job.address && <div className="text-xs text-[#5C5C5C]">{job.address}</div>}
          </InfoRow>
          {job.contact && <InfoRow icon={<Phone className="w-4 h-4" />} label="Contact">{job.contact}</InfoRow>}

          <div className="pt-3 border-t border-[#E2DED0] space-y-2">
            {!isOwner && (
              <>
                <Button
                  data-testid="job-apply-open"
                  onClick={onApplyClick}
                  className="w-full abm-btn-primary rounded-full h-11 font-medium"
                >
                  <Send className="w-4 h-4 mr-1" /> Apply now
                </Button>
                <Button
                  data-testid="job-save-btn"
                  onClick={onSaveClick}
                  variant="outline"
                  className={`w-full rounded-full h-10 ${saved ? "bg-[#C05A3D]/10 border-[#C05A3D] text-[#C05A3D]" : ""}`}
                >
                  <Heart className={`w-4 h-4 mr-1 ${saved ? "fill-current" : ""}`} /> {saved ? "Saved" : "Save for later"}
                </Button>
                <MessageButton
                  recipientId={job.owner_id}
                  recipientName={job.owner_name || job.employer || "Employer"}
                  targetType="job"
                  targetId={job.id}
                  targetTitle={job.title}
                  size="sm"
                  variant="ghost"
                  className="w-full justify-center"
                />
                {job.apply_url && (
                  <a
                    href={job.apply_url.startsWith("http") ? job.apply_url : `https://${job.apply_url}`}
                    target="_blank" rel="noreferrer"
                    data-testid="job-external-apply"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#1E3A2F] text-[#1E3A2F] h-10 text-sm hover:bg-[#1E3A2F] hover:text-white transition"
                  >
                    External application <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </>
            )}
          </div>

          <div className="flex flex-wrap gap-2 pt-3 border-t border-[#E2DED0]">
            {isOwner ? (
              <>
                <Button data-testid="job-edit-btn" className="abm-btn-pine rounded-full" onClick={() => nav(`/post/job/${job.id}`)}><Edit className="w-4 h-4 mr-1" /> Edit</Button>
                <Button data-testid="job-delete-btn" variant="destructive" className="rounded-full" onClick={remove}><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
              </>
            ) : (
              <Dialog>
                <DialogTrigger asChild>
                  <Button data-testid="job-report-btn" variant="outline" className="rounded-full"><Flag className="w-4 h-4 mr-1" /> Report</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Report this job</DialogTitle>
                    <DialogDescription>Tell us what looks off. We'll review it and take action if needed.</DialogDescription>
                  </DialogHeader>
                  <Input placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="job-report-reason" />
                  <Textarea placeholder="Details (optional)" value={details} onChange={(e) => setDetails(e.target.value)} data-testid="job-report-details" />
                  <Button onClick={submitReport} className="abm-btn-primary rounded-full" data-testid="job-report-submit">Submit report</Button>
                </DialogContent>
              </Dialog>
            )}
            <AdminRemoveButton type="job" id={job.id} title={job.title} redirectTo="/jobs" />
          </div>
        </aside>
      </section>

      <ApplyDialog open={applyOpen} onOpenChange={setApplyOpen} job={job} />
    </div>
  );
}

function InfoRow({ icon, label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.15em] text-[#5C5C5C] mb-1 inline-flex items-center gap-1">{icon} {label}</div>
      <div className="text-[#1A1A1A]">{children}</div>
    </div>
  );
}
